<template>
  <b-modal
    id="create_ticket_modal"
    hide-header
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="md"
    centered
  >
    <div>
      <p class="h3 text-capitalize">
        {{ $t("helpdesk.ticket.create_ticket") }}
      </p>
      <hr />
      <validation-observer v-slot="{ handleSubmit }">
        <b-form
          class="auth-login-form mt-2"
          @submit.prevent="handleSubmit(onSubmit)"
          ref="createTicket"
        >
          <validation-provider #default="{ errors }" name="Email" rules="email">
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.cc')"
              label-for="title"
            >
              <v-select
                v-model="form.cc"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :taggable="true"
                :multiple="true"
              />
              <p v-if="errors" class="small text-danger">
                {{
                  errors.length > 0
                    ? $t("login.the_email_field_is_required")
                    : null
                }}
              </p>
            </b-form-group>
          </validation-provider>

          <validation-provider
            :name="$tc('helpdesk.ticket.subject', 1)"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('helpdesk.ticket.subject', 1)"
              label-for="title"
            >
              <b-form-input
                id="title"
                v-model="form.subject"
                trim
                :state="errors.length > 0 ? false : null"
              />
              <p v-if="errors" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <validation-provider
            :name="$t('helpdesk.ticket.description')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.description')"
              label-for="message"
            >
              <b-form-textarea
                id="message"
                v-model="form.message"
                rows="3"
                max-rows="6"
                :state="errors.length > 0 ? false : null"
              ></b-form-textarea>
              <p v-if="errors" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <b-form-group
            class="text-capitalize"
            :label="$t('helpdesk.ticket.attachments')"
            label-for="attachments"
          >
            <vue-dropzone
              id="dropzone_file"
              ref="dropzoneFile"
              :options="dropzoneOptions"
              class="dropzone-file"
              @vdropzone-file-added="addFile"
              @vdropzone-removed-file="removeFile"
            />
          </b-form-group>

          <validation-provider
            :name="$tc('helpdesk.group.title', 2)"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('helpdesk.group.title', 2)"
              label-for="message"
            >
              <v-select
                v-model="form.group"
                :options="groupsOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="id_group"
              />
              <p v-if="errors" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <b-row>
            <b-col md="6">
              <validation-provider
                :name="$t('helpdesk.ticket.type')"
                rules="required"
                v-slot="{ errors }"
              >
                <b-form-group
                  class="text-capitalize"
                  :label="$t('helpdesk.ticket.type')"
                  label-for="message"
                >
                  <v-select
                    v-model="form.ticket_type"
                    :options="ticketTypesOptions"
                    :clearable="false"
                    :reduce="(val) => val.value"
                    input-id="id_ticket_type"
                  />
                  <p v-if="errors" class="small text-danger">
                    {{ errors[0] }}
                  </p>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider
                :name="$t('helpdesk.ticket.priority')"
                rules="required"
                v-slot="{ errors }"
              >
                <b-form-group
                  class="text-capitalize"
                  :label="$t('helpdesk.ticket.priority')"
                  label-for="message"
                >
                  <v-select
                    v-model="form.priority"
                    :options="priorityOptions"
                    :clearable="false"
                    :reduce="(val) => val.value"
                    input-id="role"
                  />
                  <p v-if="errors" class="small text-danger">
                    {{ errors[0] }}
                  </p>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-button
            size="sm"
            type="submit"
            variant="primary"
            class="text-capitalize float-right"
            :disabled="buttonSave"
          >
            <span class="align-middle text-capitalize">
              {{ $t("actions.add") }}
            </span>

            <b-spinner v-if="buttonSave" small type="grow" class="ml-50" />
          </b-button>
          <b-button
            size="sm"
            type="button"
            variant="outline-secondary"
            @click="onCancel()"
            class="mr-1 text-capitalize float-right"
          >
            {{ $t("actions.cancel") }}
          </b-button>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
} from "bootstrap-vue";
import store from "@/store";
import vSelect from "vue-select";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import { getUserData } from "@/auth/utils";

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    formValidation,
    vueDropzone: vue2Dropzone,
  },
  props: {
    ticket: {
      type: Object,
    },
  },
  data() {
    return {
      required,
      email,
      form: {
        name: "",
        userId: "",
        email: "",
        cc: [],
        subject: "",
        message: "",
        group: "",
        ticket_type: "",
        priority: "",
        file: [],
      },
      dropzoneOptions: {
        url: "https://httpbin.org/post",
        paramName: "image",
        autoProcessQueue: false,
        dictDefaultMessage: "",
        addRemoveLinks: true,
        dictRemoveFile: this.$t("actions.delete"),
        maxFilesize: 10,
        acceptedFiles: "image/*",
      },
      urlUvdesk: window.__env.VUE_APP_UVDESK,
      priorityOptions: [
        { value: "Low", label: "Low" },
        { value: "Medium", label: "Medium" },
        { value: "High", label: "High" },
        { value: "Urgent", label: "Urgent" },
      ],
      buttonSave: false,
      groupsOptions: [],
      ticketTypesOptions: [],
    };
  },
  setup() {
    const toast = useToast();
    return {
      toast,
    };
  },
  async mounted() {
    this.setUserData();

    await Promise.all([
      store.dispatch("helpdesk_group/getAll"),
      store.dispatch("helpdesk_ticket_types/getAll"),
    ]);

    this.groupsOptions = store.getters["helpdesk_group/getSelectedItems"];
    this.ticketTypesOptions =
      store.getters["helpdesk_ticket_types/getSelectedItems"];
  },
  methods: {
    setUserData() {
      const userData = getUserData();
      this.form.name = userData.name;
      this.form.userId = userData.id;
      this.form.email = userData.email;
    },
    onSubmit() {
      if (!this.form.name || !this.form.userId || !this.form.email) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Datos incompletos",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }
      this.buttonSave = true;
      let formData = new FormData();
      formData.append("name", this.form.name);
      formData.append("from", this.form.email);
      formData.append("subject", this.form.subject);
      formData.append("message", this.form.message);
      formData.append("userId", this.form.userId);
      formData.append("group", this.form.group);
      formData.append("priority", this.form.priority);
      formData.append("id_ticket_type", this.form.ticket_type);

      if (this.form.cc.length > 0) {
        this.form.cc.forEach((item) => {
          formData.append("cc[]", item);
        });
      }

      if (this.form.file.length > 0) {
        this.form.file.forEach((item) => {
          formData.append("attachments[]", item);
        });
      }

      store
        .dispatch("helpdesk_ticket/add", formData)
        .then((response) => {
          if (response.success) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            this.buttonSave = false;
            store.dispatch("helpdesk_ticket/getAssigned", {
              assigned: this.form.userId,
              id_user: this.form.userId,
            });
            this.onCancel();
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          this.buttonSave = false;
          this.toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },
    addFile(file) {
      this.form.file.push(file);
    },
    removeFile() {
      this.$refs.dropzoneFile.removeAllFiles();
      this.form.file = [];
    },
    onCancel() {
      this.$bvModal.hide("create_ticket_modal");
      this.form.cc = [],
      this.form.subject = "",
      this.form.message = "",
      this.form.group = "",
      this.form.ticket_type = "",
      this.form.priority = "",
      this.removeFile();
    },
  },
};
</script>

<style lang="scss" scoped>
.dropzone-file {
  height: 30px !important;
  padding: 0px;
  margin: auto;
  border: none;
  display: flex;
  box-shadow: inset 0 0 0 3px #dfdfdf;
}
</style>
